import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";

import { SOUND_NAME } from "../../../sounds";
import anime from "animejs/lib/anime.es.js";
import PotCollectionOutroDialogController from "./PotCollectionOutroDialogController";
import { safeInit, attemptFullscreenToggle } from "../../../utilities";
import { selectShowPotCollectionOutro } from "../../../State/Slices/animationsSlice";
import {
  selectPotCollectionCurrentRound,
  selectBet,
} from "../../../State/selectors";
import { soundManager } from "../../../SoundManager";



const PotCollectionOutroDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showPotCollectionOutro = useSelector(selectShowPotCollectionOutro);
  const potCollectionCurrentRound = useSelector(
    selectPotCollectionCurrentRound,
  );
  const bet = useSelector(selectBet);

  const outroControllerRef = useRef(null);
  const outroContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const handleUserDismissRef = useRef(null);
  const prevShowOutroRef = useRef(false);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;

  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: POPUP_OVERLAY_CONFIG.ALPHA,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: POPUP_OVERLAY_CONFIG.ALPHA },
        alpha: 0,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once
  useEffect(() => {
    if (outroContainerRef.current && !outroControllerRef.current) {
      safeInit(() => {
        outroControllerRef.current = new PotCollectionOutroDialogController(
          outroContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
        );
        // "TAP TO CONTINUE" text routes through the same dismiss flow (fullscreen
        // + sound + close) as the full-screen overlay. Proxy via ref so it always
        // calls the latest handleUserDismiss, not a stale mount-time closure.
        outroControllerRef.current.setButtonClickHandler((...args) => {
          if (handleUserDismissRef.current) handleUserDismissRef.current(...args);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (outroControllerRef.current) {
      outroControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  useEffect(() => {
    if (outroControllerRef.current && showPotCollectionOutro) {
      const totalWin = potCollectionCurrentRound?.win || 0;
      outroControllerRef.current.setTotalWinValue(totalWin, bet);
      outroControllerRef.current.startSequence();
    }
  }, [showPotCollectionOutro, potCollectionCurrentRound, bet]);

  // Track previous state
  useEffect(() => {
    prevShowOutroRef.current = showPotCollectionOutro;
  }, [showPotCollectionOutro]);

  // Handle user click: fullscreen (if needed) + dismiss
  const handleUserDismiss = useCallback(
    async (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      await attemptFullscreenToggle(dispatch, device);

      if (outroControllerRef.current && showPotCollectionOutro) {
        const accepted = outroControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showPotCollectionOutro, dispatch, device],
  );

  // Keep ref current so the controller's proxy always calls the latest version
  handleUserDismissRef.current = handleUserDismiss;

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showPotCollectionOutro || !outroControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        await handleUserDismiss(event);
      } catch {
        if (
          outroControllerRef.current &&
          outroControllerRef.current.finishAnimation
        ) {
          outroControllerRef.current.finishAnimation();
        }
      }
    };

    if (showPotCollectionOutro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showPotCollectionOutro, handleUserDismiss]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (outroControllerRef.current) {
        outroControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(POPUP_OVERLAY_CONFIG.COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="pot_collection_outro"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      eventMode={showPotCollectionOutro ? "auto" : "none"}
      ref={outroContainerRef}
    >
      {/* Background overlay */}
      <Graphics
        name="pot_collection_outro_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showPotCollectionOutro ? "static" : "none"}
        pointerdown={handleUserDismiss}
        cursor="default"
      />

      {/* Spine animation, counter, and button managed internally by PotCollectionOutroDialogController */}
    </Container>
  );
};

export default memo(PotCollectionOutroDialog);
